import React, { memo, useEffect, useMemo, useState } from 'react';
import Common from 'src/components/Common';
import reactQueryKeys from 'src/constants/reactQueryKeys';
import { useFetch } from 'src/hooks/useFetch';
import { useTwilioChannel, useTwilioClient, useTwilioMessage } from '@chatgenie/chat-utils';
import EmptyState from '../EmptyState';
import ServiceCard from './components/ServiceCard';
import styles from './styles';
import { chatgenieAPI } from 'src/lib/api/chatgenieApi';
import { reportError } from 'src/utils/monitoring';
import { queryCache } from 'react-query';
import useChatState from 'src/screens/Chat/hooks/useChatState';
import { normalizeTicket } from 'src/utils/normalizer';
import { Alerts as ErrorMessage } from 'src/components/ConnectionIndicator';
import Header from './Header';
import useTranslation from 'src/hooks/useTranslation';

const getValidTickets = (tickets) => {
  if (!tickets) return [];
  return tickets
    .filter((message) => Boolean(message.attributes?.ticket))
    .map((message) => message.attributes.ticket)
    .slice()
    .reverse();
};

const TwilioServiceList = () => {
  const { translate } = useTranslation();
  const [filterStatus, setFilterStatus] = useState({
    label: translate('header.status.active'),
    value: 0,
  });
  const { chatState, saveTicket } = useChatState();

  const { data: channels } = useFetch(reactQueryKeys.CHANNELS, () =>
    chatgenieAPI.getChannels({
      per_page: 50,
      'filter-type': 'i=user',
      'filter-platform': 'twilio',
    }),
  );

  const { client } = useTwilioClient();
  const { channel, connectChannel } = useTwilioChannel();
  const [isLoadingChannel, setIsLoadingChannel] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  const {
    messages,
    loadMore,
    isLoading: ticketsLoading,
    isFetchingMore: isFetching,
  } = useTwilioMessage({
    channel,
    initialLoading: true,
  });

  const allValidTickets = useMemo(() => {
    return getValidTickets(messages);
  }, [messages]);

  const tickets = useMemo(() => {
    return allValidTickets.filter(
      (ticket) => Boolean(ticket.statusObject?.closed_status) === Boolean(filterStatus.value),
    );
  }, [allValidTickets, filterStatus]);

  const hasTickets = useMemo(
    () => getValidTickets(messages || [])?.length > 0 && !ticketsLoading,
    [messages, ticketsLoading],
  );

  useEffect(() => {
    queryCache.setQueryData(reactQueryKeys.TICKETS, allValidTickets);
    const currentLiveThread = allValidTickets?.find((el) => el.id === chatState.ticket?.id);
    if (currentLiveThread) {
      saveTicket(normalizeTicket(currentLiveThread));
    }
  }, [allValidTickets]);

  useEffect(() => {
    if (client && channels) {
      const channelUniqueName = channels.data.data[0]?.name;
      if (!channelUniqueName) {
        reportError('No valid channel to connect');
        return;
      }
      setIsLoadingChannel(true);
      setErrorMessage('');
      connectChannel(channelUniqueName)
        .catch((err) => {
          console.error(`Unable to load channel: ${channelUniqueName} - ${err.message}`);
          reportError(`Unable to load channel: ${channelUniqueName} - ${err.message}`);
          setIsLoadingChannel(false);
          setErrorMessage(`Unable to load threads: ${err.message}`);
        })
        .finally(() => {
          setIsLoadingChannel(false);
        });
    }
  }, [client, channels]);

  const ListEmptyComponent =
    !errorMessage && (ticketsLoading || isLoadingChannel) ? <Common.Spinner size="large" /> : <EmptyState />;

  return (
    <Common.View flexGrow={1} transparent>
      <Common.Text>{errorMessage && <ErrorMessage title={errorMessage} />}</Common.Text>
      <Common.FlatList
        ListHeaderComponent={
          <Header hasTickets={hasTickets} setFilterStatus={setFilterStatus} filterStatus={filterStatus} />
        }
        data={tickets || []}
        ListEmptyComponent={ListEmptyComponent}
        refreshing={!ticketsLoading && isFetching}
        // onRefresh={refetch}
        scrollEventThrottle={16}
        style={styles.list}
        contentContainerStyle={[styles.contentContainer, tickets.length && { paddingBottom: 60 }]}
        onEndReachedThreshold={0.5}
        onEndReached={loadMore}
        keyExtractor={(item) => item.id?.toString()}
        renderItem={({ item }) => <ServiceCard item={item} />}
        ListFooterComponent={isFetching && <Common.Spinner />}
      />
    </Common.View>
  );
};

export default memo(TwilioServiceList);
